import React, { useState, useEffect, useRef } from 'react';
import './carousel.css';

// Import professional signup images (reusing same images)
import hairdresserImage from '../../assets/images/professional-signup/hairdresser.png';
import nailTechImage from '../../assets/images/professional-signup/nail-tech.png';
import barberImage from '../../assets/images/professional-signup/barber.png';
import appointmentImage from '../../assets/images/professional-signup/appointment.png';

// Define facts outside component to prevent recreation on every render
// This is critical for iOS Safari which has smaller call stack limits
const ALL_FACTS = [
  {
    title: "Did You Know?",
    subtitle: "Hair grows about 6 inches per year on average - that's roughly 0.5 inches per month!",
    image: hairdresserImage
  },
  {
    title: "Beauty Fact",
    subtitle: "The average person has about 100,000 to 150,000 hair follicles on their head.",
    image: nailTechImage
  },
  {
    title: "Barbering History",
    subtitle: "The barber pole's red and white stripes date back to when barbers also performed surgery!",
    image: barberImage
  },
  {
    title: "Hair Science",
    subtitle: "A single strand of hair can support up to 100 grams of weight - that's stronger than copper wire!",
    image: appointmentImage
  },
  {
    title: "Industry Insight",
    subtitle: "The global beauty industry is worth over £400 billion and growing every year.",
    image: hairdresserImage
  },
  {
    title: "Fun Fact",
    subtitle: "Blonde hair has more strands than dark hair - about 140,000 vs 100,000 on average.",
    image: nailTechImage
  },
  {
    title: "Nail Knowledge",
    subtitle: "Fingernails grow about 3-4 times faster than toenails and faster in summer than winter.",
    image: barberImage
  },
  {
    title: "Style History",
    subtitle: "The bob haircut became popular in the 1920s as a symbol of women's liberation.",
    image: appointmentImage
  },
  {
    title: "Skin Science",
    subtitle: "Your skin completely renews itself every 28 days - that's 1,000 new skins in a lifetime!",
    image: hairdresserImage
  },
  {
    title: "Barbering Tradition",
    subtitle: "The word 'barber' comes from the Latin 'barba' meaning beard.",
    image: barberImage
  },
  {
    title: "Hair Care Tip",
    subtitle: "Wet hair can stretch up to 30% of its original length without damage.",
    image: nailTechImage
  },
  {
    title: "Beauty Business",
    subtitle: "The average salon client visits every 6-8 weeks for hair services.",
    image: appointmentImage
  }
];

// Helper function to shuffle and pick random facts - defined outside component
const getRandomFacts = (count = 4) => {
  const shuffled = [...ALL_FACTS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const LoginCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track if slides have been initialized to prevent re-initialization
  const isInitializedRef = useRef(false);
  
  // Initialize slides only once using useMemo with empty dependency
  // This prevents the infinite loop that was occurring on iOS
  const [slides, setSlides] = useState(() => {
    // Initialize with random facts immediately to avoid flash of empty content
    return getRandomFacts(4);
  });

  // Mark as initialized and set up cleanup
  useEffect(() => {
    isMountedRef.current = true;
    isInitializedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Carousel auto-advance effect
  useEffect(() => {
    // Guard: Don't set up interval if no slides
    if (slides.length === 0) return;
    
    const slideCount = slides.length;
    
    const interval = setInterval(() => {
      if (isMountedRef.current) {
        setCurrentSlide((prev) => (prev + 1) % slideCount);
      }
    }, 6000); // Slightly longer to read the facts
    
    return () => clearInterval(interval);
  }, [slides.length]); // Only re-run if slide count changes (which it won't after init)

  // Early return if no slides (shouldn't happen with new initialization)
  if (slides.length === 0) return null;

  return (
    <div className="auth-carousel">
      {slides.map((slide, index) => (
        <div 
          key={index} 
          className={`auth-carousel-slide ${index === currentSlide ? 'active' : ''}`}
          style={{ backgroundImage: `url(${slide.image})` }}
        >
          <div className="auth-carousel-overlay">
            <div className="auth-carousel-content">
              <h2>{slide.title}</h2>
              <p>{slide.subtitle}</p>
            </div>
          </div>
        </div>
      ))}
      
      <div className="auth-carousel-indicators">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`auth-carousel-indicator ${index === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default LoginCarousel;